import { gql } from 'apollo-boost';
import { useQuery } from 'react-apollo';
import { removeConnectionsMemoized } from '..';
import { useMutation } from 'shared/apollo';
import {
  Beneficiaries,
  Beneficiary,
  BeneficiaryType,
  BeneficiaryQuery
} from 'npo/pages/ReportBuilder/Components/Beneficiary/utils/beneficiaryInterfaces';
import { BENEFICIARY_CATEGORIES_KEYS } from 'npo/pages/ReportBuilder/Components/Beneficiary/utils/helperFunctions';
import { transformToBeneficiaries } from 'npo/pages/ReportBuilder/Components/Beneficiary/utils/transformToBeneficiaries';

const query = gql`
  query useReportBuilder($id: ID) {
    allReports(filters: { id: $id }) {
      edges {
        node {
          beneficiaryDemographics {
            edges {
              node {
                id
                bdtName
                demographicId
                category
                description
                percentage
              }
            }
          }
        }
      }
    }
  }
`;

const mutation = gql`
  mutation beneficiaryDemographic(
    $reportId: Int!
    $beneficiaries: [BeneficiaryInput]!
  ) {
    addOrUpdateBeneficiary(reportId: $reportId, beneficiaries: $beneficiaries) {
      success
    }
  }
`;

const convertBeneficiariesToArray = (
  beneficiaries: Beneficiaries
): Beneficiary<BeneficiaryType>[] => {
  const result: Beneficiary<BeneficiaryType>[] = [];
  BENEFICIARY_CATEGORIES_KEYS.forEach(b => {
    const beneficiary = beneficiaries[b];
    if (beneficiary) {
      beneficiary.categories.forEach((c: Beneficiary<BeneficiaryType>) => {
        result.push({
          name: c.name,
          // query sometimes sends `null` which were saved in v3
          // but mutation does not accept anything but Float.
          percentage: c.percentage || 0
        });
      });
    }
  });
  return result;
};

export const useBeneficiariesMutation = () => {
  const { data, isLoading, mutate } = useMutation();
  const run = (
    reportId: number,
    beneficiaries: Beneficiaries,
    onFinish?: () => unknown
  ) => {
    const variables = {
      reportId,
      beneficiaries: convertBeneficiariesToArray(beneficiaries)
    };
    mutate(mutation, variables, () => {
      onFinish?.();
    });
  };
  return { data, isLoading, run };
};

interface BeneficiaryQueryReturn extends Omit<BeneficiaryQuery, 'data'> {
  data: Beneficiaries;
}

export const useAllReportBeneficiaries = (
  reportId: number
): BeneficiaryQueryReturn => {
  const { data: rawData, refetch, loading, error } = useQuery(query, {
    fetchPolicy: 'no-cache',
    variables: { id: reportId }
  });
  const beneficiary =
    rawData?.allReports?.edges.length > 0
      ? removeConnectionsMemoized(rawData?.allReports)[0]
          ?.beneficiaryDemographics || []
      : [];
  return {
    data: transformToBeneficiaries(beneficiary),
    refetch,
    loading,
    error
  };
};
